import * as React from "react";
import { toast } from "sonner";
import { ContentLibrary } from "./components/ContentLibrary";
import { EditProgramPanel } from "./components/EditProgramPanel";
import { EditProgramPanelSkeleton } from "./components/EditProgramPanelSkeleton";
import { SchedulerGridSkeleton } from "./components/SchedulerGridSkeleton";
import { SchedulerHeader } from "./components/SchedulerHeader";
import { TimelineGrid } from "./components/TimelineGrid";
import { TrimDialog } from "./components/TrimDialog";
import { WeekGrid } from "./components/WeekGrid";
import { getWeekDateKeys, toMinutes } from "./schedulerUtils";
import type { ContentItem, DragPayload, PlacementResult } from "./types";
import { useChannelScheduler } from "./useChannelScheduler";
import { useTouchDragBridge } from "./useTouchDragBridge";
import { useUploads } from "./useUploads";
import { useVodGames } from "./useVodGames";
// HIGHLIGHTS TAB HIDDEN: uncomment these three imports and the blocks marked
// the same way below to bring back the Highlights tab (whizzards).
// import { useWhizzards, type WhizzardTypeFilter } from "./useWhizzards";
// import { scopeToChannels } from "./channelFilter";
// import { LIVE_CHANNEL_IDS } from "@/v2/lib/liveChannels";

/** Admin "Scheduler" tab: 24/7 channel timeline, content library and program editor. */
/** Debounce so the API `search` call fires once typing pauses, not per keystroke. */
const SEARCH_DEBOUNCE_MS = 300;

export function ChannelSchedulerPage() {
  const [channelFilter, setChannelFilter] = React.useState("");
  const [searchInput, setSearchInput] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const [uploadSearch, setUploadSearch] = React.useState("");
  const [debouncedUploadSearch, setDebouncedUploadSearch] = React.useState("");
  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedUploadSearch(uploadSearch), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [uploadSearch]);

  // HIGHLIGHTS TAB HIDDEN
  // const [whizzardSearch, setWhizzardSearch] = React.useState("");
  // const [debouncedWhizzardSearch, setDebouncedWhizzardSearch] = React.useState("");
  // React.useEffect(() => {
  //   const timer = window.setTimeout(() => setDebouncedWhizzardSearch(whizzardSearch), SEARCH_DEBOUNCE_MS);
  //   return () => window.clearTimeout(timer);
  // }, [whizzardSearch]);
  // const [whizzardType, setWhizzardType] = React.useState<WhizzardTypeFilter>("all");

  const vod = useVodGames(channelFilter, debouncedSearch);
  // HIGHLIGHTS TAB HIDDEN
  // The Highlights tab shares the Games tab's channel dropdown, scoped to the
  // 24x7 channels so "all" never asks for another channel's highlights.
  // const whizzards = useWhizzards(
  //   scopeToChannels(channelFilter, LIVE_CHANNEL_IDS),
  //   debouncedWhizzardSearch,
  //   whizzardType,
  // );
  const uploads = useUploads(debouncedUploadSearch);
  // Only ready uploads join the schedulable content: a video still
  // uploading or transcoding has no manifest, so it can reach the timeline
  // only once it's done.
  const schedulable = React.useMemo(
    () => [
      ...vod.games,
      // HIGHLIGHTS TAB HIDDEN
      // ...whizzards.items,
      ...uploads.items.filter((item) => item.uploadStatus === "ready"),
    ],
    // HIGHLIGHTS TAB HIDDEN: add whizzards.items back to these deps.
    [vod.games, uploads.items],
  );
  const s = useChannelScheduler(schedulable);
  const dragRef = React.useRef<DragPayload | null>(null);
  const touchDrag = useTouchDragBridge(dragRef);
  const [now, setNow] = React.useState(() => new Date());
  const [trimOpen, setTrimOpen] = React.useState(false);

  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const report = (result: PlacementResult) => {
    if (!result.ok && result.error) toast.error(result.error);
    return result.ok;
  };

  const noteReplaced = (result: PlacementResult) => {
    if (result.ok && result.replaced) {
      toast.info(`Replaced ${result.replaced} program${result.replaced === 1 ? "" : "s"} in that slot.`);
    }
    return result;
  };

  const trim = (id: string, inSec: number, outSec: number) =>
    noteReplaced(s.setTrim(id, inSec, outSec));

  const handleResizeEdge = (id: string, edge: "start" | "end", deltaMin: number) =>
    report(s.resizeTrim(id, edge, deltaMin));

  const contentById = (id: string): ContentItem | undefined => s.content.find((c) => c.id === id);

  const channelName = (id: string) => s.channels.find((c) => c.id === id)?.name ?? "another channel";

  // A game (or a whizzard cut from one) is licensed to air on its own
  // channel only, so a drop onto a different channel's row is refused with
  // a plain-language reason instead of silently landing (or doing nothing).
  // Games carry it as broadcast_platform_channel_id, whizzards as ChannelID.
  const gameChannelId = (source?: Record<string, unknown>): string | undefined => {
    const id = source?.broadcast_platform_channel_id ?? source?.ChannelID;
    return id == null ? undefined : String(id);
  };
  const checkChannel = (fromChannelId: string | undefined, targetChannelId: string): boolean => {
    if (!fromChannelId || fromChannelId === targetChannelId) return true;
    toast.error(`This airs on ${channelName(fromChannelId)}. Drop it onto the ${channelName(fromChannelId)} row instead.`);
    return false;
  };

  const handleDropAt = ({
    channelId,
    date,
    startMin,
  }: {
    channelId: string;
    date: string;
    startMin: number;
  }) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    if (drag.kind === "program") {
      const program = s.programs.find((p) => p.id === drag.id);
      if (!checkChannel(gameChannelId(program?.source), channelId)) return;
      report(s.movePlacement(drag.id, channelId, date, startMin));
      return;
    }
    const item = contentById(drag.id);
    if (!item) return;
    if (!checkChannel(item.channelId, channelId)) return;
    report(noteReplaced(s.addContent(item, channelId, date, startMin)));
  };

  const handleDropOnDay = ({ channelId, date }: { channelId: string; date: string }) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    if (drag.kind === "program") {
      const program = s.programs.find((p) => p.id === drag.id);
      if (!program) return;
      if (!checkChannel(gameChannelId(program.source), channelId)) return;
      report(s.movePlacement(program.id, channelId, date, toMinutes(program.startTime)));
      return;
    }
    const item = contentById(drag.id);
    if (!item) return;
    if (!checkChannel(item.channelId, channelId)) return;
    report(s.addContent(item, channelId, date, null));
  };

  return (
    <div className="sched-root flex flex-col" style={{ gap: 24, padding: "8px 4px" }}>
      <SchedulerHeader date={s.date} onSelectDate={s.setDate} onShift={s.shiftDate} />

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {s.isLoading ? (
          <SchedulerGridSkeleton />
        ) : s.view === "day" ? (
          <TimelineGrid
            channels={s.channels}
            programs={s.programs}
            dates={s.stripDays}
            openDate={s.date}
            todayKey={s.todayKey}
            now={now}
            selectedId={s.selectedId}
            dragRef={dragRef}
            touchDrag={touchDrag}
            onSelect={s.select}
            onDropAt={handleDropAt}
            onResizeEdge={handleResizeEdge}
          />
        ) : (
          <WeekGrid
            channels={s.channels}
            programs={s.programs}
            dateKeys={getWeekDateKeys(s.date)}
            todayKey={s.todayKey}
            selectedDate={s.date}
            selectedId={s.selectedId}
            dragRef={dragRef}
            touchDrag={touchDrag}
            onSelect={s.select}
            onOpenDay={(date) => {
              s.setDate(date);
              s.setView("day");
            }}
            onDropOnDay={handleDropOnDay}
          />
        )}
      </div>

      <div className="sched-bottom">
        <ContentLibrary
          content={s.content}
          dragRef={dragRef}
          touchDrag={touchDrag}
          gamesLoading={vod.isLoading}
          hasMoreGames={vod.hasNextPage}
          loadingMoreGames={vod.isFetchingNextPage}
          onLoadMoreGames={vod.fetchNextPage}
          channels={vod.channels}
          channelFilter={channelFilter}
          onChannelFilterChange={setChannelFilter}
          onSearchChange={setSearchInput}
          uploads={uploads}
          uploadSearch={uploadSearch}
          onUploadSearchChange={setUploadSearch}
          // HIGHLIGHTS TAB HIDDEN
          // whizzards={whizzards}
          // whizzardSearch={whizzardSearch}
          // onWhizzardSearchChange={setWhizzardSearch}
          // whizzardType={whizzardType}
          // onWhizzardTypeChange={setWhizzardType}
        />
        {s.isLoading ? (
          <EditProgramPanelSkeleton />
        ) : (
          <EditProgramPanel
            program={s.selected}
            onTrim={trim}
            onSetTitle={s.setTitle}
            onSetDescription={s.setDescription}
            onRemove={s.remove}
            onPreview={() => setTrimOpen(true)}
            onSave={() => {
              void s.saveNow().then((ok) => {
                if (ok) toast.success("Schedule saved.");
              });
            }}
            onError={(message) => toast.error(message)}
          />
        )}
      </div>

      {s.selected ? (
        <TrimDialog
          program={s.selected}
          open={trimOpen}
          onOpenChange={setTrimOpen}
          onApply={(inSec, outSec) => report(trim(s.selected!.id, inSec, outSec))}
        />
      ) : null}

      {/* Touch drag ghost: a finger occludes whatever it's over, so the item
          being placed floats just above it instead. */}
      {touchDrag.ghost ? (
        <div
          aria-hidden
          className="pointer-events-none fixed z-50 truncate rounded-md bg-slate-900 text-xs font-medium text-white shadow-lg"
          style={{
            left: touchDrag.ghost.x,
            top: touchDrag.ghost.y - 20,
            transform: "translate(-50%, -100%)",
            maxWidth: 220,
            padding: "6px 10px",
          }}
        >
          {touchDrag.ghost.label}
        </div>
      ) : null}
    </div>
  );
}
